import { useEffect, useMemo, useState } from 'react';

export const DEFAULT_PAGE_SIZE = 10;

export interface ClientPagination<T> {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  total: number;
  totalPages: number;
  pageItems: T[];
}

interface UseClientPaginationOptions {
  pageSize?: number;
  /**
   * Any value derived from the active search / filter / sort controls. When it
   * changes the list returns to page 1, as required for filtered result sets.
   */
  resetKey?: string | number;
}

/**
 * Client-side pagination for lists that are already loaded in full. Keeps the
 * current page within bounds and resets to page 1 whenever `resetKey` changes.
 */
export function useClientPagination<T>(
  items: T[],
  { pageSize = DEFAULT_PAGE_SIZE, resetKey }: UseClientPaginationOptions = {},
): ClientPagination<T> {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize],
  );

  return { page: safePage, setPage, pageSize, total, totalPages, pageItems };
}
